import { alpha, Box, Divider, Stack, Tooltip } from '@mui/material'
import TimeBuckets from '../../../components/timeBuckets/TimeBuckets'
import ChartTypeMenu from '../../../components/chartTypeMenu/ChartTypeMenu'
import AlertMenuItem from '../../../components/alerts/alertMenuItem/AlertMenuItem'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import * as indicatorProto from '../../../proto/generated/indicator'
import Indicators from '../../../components/indicators/Indicators'
import { memo, useMemo,  } from 'react'
import { MULTI_STRIKE_MIN_BUCKET_SECONDS, MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import SecurityCompare from '../../../components/securitySearch/SecurityCompare'
import { selectActiveLayoutId } from '../../../selectors/home'
import {
    selectActiveChartId,
    selectChartConfig
} from '../../../selectors/chart'
import { selectActiveWidget } from '../../../selectors/widgetDashboard'
import { setMultiPaneFullScreen } from '../../../actions/common.actions'
import { WidgetTypes } from '../../../components/widgetDashboard/widgetDashboard.enums'
import { useStyles } from './styles'
import { ExtraSmallButton } from '../../../components/custom'
import FullScreenIcon from '../../../assets/icons/FullScreenIcon'
import UndoRedoButtons from '../../../components/UndoRedoFunctonalities/UndoRedoButtons'
import ChartReplayButton from '../../../components/chartV2/chartReplayButton/chartReplayButton'
import { fullScreenModeTooltipTitle } from '../../../components/KeyboardShortcuts/FullScreenModeTooltipTitle'


export type StockBarActionsProps = {
    disableChartOptions: boolean
    disableAlertButton: boolean
    onIndicatorSelected: Function
}

const StockBarActions: React.FC<StockBarActionsProps> = 
    ({
        disableChartOptions,
        disableAlertButton,
        onIndicatorSelected
    }) => {
        const { classes } = useStyles()
        const indicators: indicatorProto.Indicator[] = useSelector((state: RootState) => state.chartStore.indicators)
        const activeChartId = useSelector(selectActiveChartId)
        const activeLayoutId = useSelector(selectActiveLayoutId)
        const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
        const chartConfig = useSelector(selectChartConfig(activeChartId))
        const dispatch = useDispatch()
        const handleToggleFullScreen = useMemo(() => {
            return () => {
                const element = document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)
                if (element) {
                    if (!document.fullscreenElement) {
                        element
                            .requestFullscreen()
                            .then(() => {
                                dispatch(setMultiPaneFullScreen(true))
                            })
                            .catch((err) => {
                                console.error(`Error entering fullscreen: ${err.message}`)
                            })
                    }
                }
            }
        }, [dispatch])
        const divider = useMemo(() => <Divider orientation="vertical" variant={'middle'} flexItem
                                               sx={{ backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.05) }} />, [])
        const isReady = chartConfig && chartConfig.chartType && chartConfig.timeBucket
        const forceDisabled = !isReady
        const effectiveDisableChartOptions = disableChartOptions || forceDisabled
        const effectiveDisableAlertButton = disableAlertButton || forceDisabled
        const actionItems = useMemo(() => {
            return [
                <SecurityCompare viewKey={activeChartId} disabled={effectiveDisableChartOptions} key={`security-compare`} />,
                <TimeBuckets
                    id={'stockbar-timebucket-menu-button'}
                    registerShortcut={true}
                    disabled={effectiveDisableChartOptions && (activeWidget ? ![WidgetTypes.MULTI_STRIKE_CHART, WidgetTypes.OI_CHART, WidgetTypes.OPEN_INTEREST_CHART, WidgetTypes.OPTION_CHAIN].includes(activeWidget?.widgetType) : true)}
                    viewKey={activeChartId}
                    key={'stockbar-timebucket-menu-button'}
                    showFavorites={true}
                    minBucketSeconds={
                        activeWidget?.widgetType === WidgetTypes.MULTI_STRIKE_CHART
                            ? MULTI_STRIKE_MIN_BUCKET_SECONDS
                            : undefined
                    }
                />,
                <ChartTypeMenu disabled={effectiveDisableChartOptions} viewKey={activeChartId} key="chart-type-menu" />,
                <Indicators indicators={indicators} onIndicatorClicked={onIndicatorSelected}
                            disabled={effectiveDisableChartOptions} title={''} key="indictaors-menu" />,
                <AlertMenuItem viewKey={activeChartId} disabled={effectiveDisableAlertButton} key="alerts-menu" />,
                <Tooltip
                    title={fullScreenModeTooltipTitle}
                    arrow
                    placement="bottom"
                    key="fullscreen-button"
                >
                    <ExtraSmallButton data-event={'Full screen clicked'} data-event-source={'stockbar'} size="small"
                        color="inherit" onClick={handleToggleFullScreen} key="full-screen-button">
                        <FullScreenIcon fontSize={"small"}/>
                    </ExtraSmallButton>
                </Tooltip>,
                <ChartReplayButton viewKey={activeChartId} key="chart-replay-button" />
            ]
        }, [activeChartId, effectiveDisableChartOptions, activeWidget, indicators, onIndicatorSelected, effectiveDisableAlertButton, handleToggleFullScreen])

        return (
            <Stack
                id="stock-bar-actions"
                position={'relative'}
                className={classes.actionsContainer}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                width={'fitContent'}
                divider={divider}
            >
                {actionItems}
                <UndoRedoButtons />
            </Stack>
        )
    }

export default memo(StockBarActions)
